"use client";

import { UploadZone } from "@/components/ui/UploadZone";
import { LogoMarquee } from "@/components/ui/LogoMarquee";
import { siteConfig } from "@/config/site";
import { landingContent } from "@/content/landing";
import { getHeroBrandLogos } from "./brand-logos";
import { HeroFloatingBadges } from "./HeroFloatingBadges";

function preventOrphan(text: string, lastWords = 2): React.ReactNode {
  const words = text.split(' ');
  if (words.length <= lastWords) return text;
  const splitAt = words.length - lastWords;
  return (
    <>
      {words.slice(0, splitAt).join(' ')}{' '}
      <span style={{ whiteSpace: 'nowrap', display: 'inline-block' }}>
        {words.slice(splitAt).join(' ')}
      </span>
    </>
  );
}

/** Split by sentence boundaries (., !, ?) and protect last N words of each sentence */
function preventOrphanBySentence(text: string, lastWords = 2): React.ReactNode {
  const sentences = text.split(/(?<=[.!?])\s+/);
  if (sentences.length <= 1) return preventOrphan(text, lastWords);
  return (
    <>
      {sentences.map((s, i) => (
        <span key={i}>
          {i > 0 && ' '}
          {preventOrphan(s, lastWords)}
        </span>
      ))}
    </>
  );
}

/* Exact Figma positions — absolute, rotated (desktop only).
   `top` is relative to the upload-card wrapper (card top = 0), so badges
   travel with the card regardless of H1/subtitle length. */
interface HeroProps {
  className?: string;
  onFileSelect?: (file: File) => void;
  onUrlSubmit?: (url: string) => void;
  /** Override H1 title. Default: "Транскрибация аудио и видео\nв текст с ИИ" */
  title?: string;
  /** Override subtitle. Default: main landing subtitle */
  subtitle?: string;
  /** Default upload tab */
  uploadTab?: "file" | "url";
}

export function Hero({ className = "", onFileSelect, onUrlSubmit, title, subtitle, uploadTab }: HeroProps) {
  const brandLogos = getHeroBrandLogos(siteConfig.siteId);

  return (
    <section id="hero" className={`relative w-full bg-white overflow-x-clip ${className}`} style={{ overflow: "clip" }}>
      {/* Ellipse background */}
      <div className="absolute left-1/2 -translate-x-1/2 top-[-52px] pointer-events-none max-w-none" style={{ width: 1580, height: 923 }}>
        <div
          aria-hidden="true"
          className="size-full"
          style={{
            background:
              "radial-gradient(ellipse 50% 50% at 50% 50%, var(--brand-primary-soft) 0%, var(--brand-primary-tint) 44%, rgba(255,255,255,0) 72%)",
          }}
        />
      </div>

      {/* ===== Shared responsive H1 + subtitle — single element for all breakpoints =====
          Gaps per Figma: title↔upload 40px, upload↔trust 40px. */}
      <div className="relative flex flex-col items-center text-center px-[20px] max-tablet:px-[16px] w-[700px] max-tablet:w-auto" style={{ gap: 16, paddingTop: 80, marginLeft: 'auto', marginRight: 'auto' }}>
        <h1
          className="text-[48px] leading-[56px] tracking-[-0.96px] max-tablet:text-[32px] max-tablet:leading-[40px] max-tablet:tracking-[-0.64px]"
          style={{ fontFamily: "var(--font-commissioner)", fontWeight: 700, fontStyle: "normal", color: "rgba(0,0,0,0.9)", fontVariationSettings: '"FLAR" 0, "VOLM" 0', margin: 0, textWrap: "balance" }}
        >
          {title ? preventOrphan(title, 2) : landingContent.hero.title}
        </h1>
        <p
          className="text-[18px] leading-[24px] tracking-[-0.36px] max-tablet:text-[16px] max-tablet:leading-[22px]"
          style={{ fontFamily: "var(--font-commissioner)", fontWeight: 500, fontStyle: "normal", color: "rgba(0,0,0,0.9)", margin: 0, maxWidth: 700, textWrap: "pretty" }}
        >
          {subtitle ? preventOrphanBySentence(subtitle, 2) : landingContent.hero.subtitle}
        </p>
      </div>

      {/* ===== Desktop layout (≥1269): upload + badges + trust. Title is shared above. ===== */}
      <div className="relative hidden desktop:flex flex-col items-center overflow-x-clip" style={{ paddingTop: 40, paddingBottom: 80 }}>
        {/* Upload card wrapper — anchors badges relative to the card itself */}
        <div className="relative">
          <UploadZone onFileSelect={onFileSelect} onUrlSubmit={onUrlSubmit} initialTab={uploadTab} />

          <HeroFloatingBadges />
        </div>

        {/* Trust section */}
        <div className="flex flex-col items-center" style={{ gap: 24, marginTop: 40 }}>
          <p style={{ fontFamily: "var(--font-commissioner)", fontWeight: 500, fontStyle: "normal", fontSize: 20, lineHeight: "24px", letterSpacing: "-0.4px", color: "rgba(0,0,0,0.9)", textAlign: "center", margin: 0 }}>
            {landingContent.hero.trustText}
          </p>
          <LogoMarquee logos={brandLogos} />
        </div>
      </div>

      {/* ===== Tablet + mobile layout (<1269): upload + trust. Title is shared above. ===== */}
      <div className="relative flex desktop:hidden flex-col items-center w-full overflow-x-clip px-[20px] max-tablet:px-[16px] pt-[40px] pb-[80px]">
        {/* Upload card */}
        <div className="w-full max-w-[600px] flex justify-center">
          <UploadZone onFileSelect={onFileSelect} onUrlSubmit={onUrlSubmit} fluid initialTab={uploadTab} />
        </div>

        {/* Trust section */}
        <div className="flex flex-col items-center w-full" style={{ gap: 24, marginTop: 40 }}>
          <p
            className="text-[20px] leading-[24px] tracking-[-0.4px] max-tablet:text-[16px] max-tablet:leading-[22px]"
            style={{ fontFamily: "var(--font-commissioner)", fontWeight: 500, fontStyle: "normal", color: "rgba(0,0,0,0.9)", textAlign: "center", margin: 0 }}
          >
            {landingContent.hero.trustText}
          </p>
          <LogoMarquee logos={brandLogos} />
        </div>
      </div>
    </section>
  );
}
